import React, { useState } from 'react';
import {
  Boxes,
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  AlertTriangle,
  Search,
  Filter,
  CheckCircle2,
  Package,
  Wrench,
  Clock,
  Sparkles,
  Layers,
} from 'lucide-react';
import { InventoryItem, InventoryLog } from '../../types';
import {
  INITIAL_INVENTORY_ITEMS,
  INITIAL_INVENTORY_LOGS,
} from '../../data/adminModulesData';

export const AdminInventoryView: React.FC = () => {
  const [items, setItems] = useState<InventoryItem[]>(INITIAL_INVENTORY_ITEMS);
  const [logs, setLogs] = useState<InventoryLog[]>(INITIAL_INVENTORY_LOGS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedItemForLog, setSelectedItemForLog] = useState<InventoryItem | null>(null);

  // New Item State
  const [newItemName, setNewItemName] = useState('');
  const [newItemCode, setNewItemCode] = useState('');
  const [newItemCategory, setNewItemCategory] = useState<InventoryItem['category']>('electrical');
  const [newItemStock, setNewItemStock] = useState(10);
  const [newItemMinStock, setNewItemMinStock] = useState(5);
  const [newItemUnit, setNewItemUnit] = useState('ชิ้น');
  const [newItemPrice, setNewItemPrice] = useState(100);
  const [newItemLocation, setNewItemLocation] = useState('ตู้ช่าง A1');

  // Stock In / Out State
  const [logType, setLogType] = useState<'in' | 'out'>('out');
  const [logQuantity, setLogQuantity] = useState(1);
  const [logRequester, setLogRequester] = useState('');
  const [logDepartment, setLogDepartment] = useState('ฝ่ายช่างซ่อมบำรุง');
  const [logPurpose, setLogPurpose] = useState('');

  const filteredItems = items.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const lowStockItems = items.filter((item) => item.current_stock <= item.min_stock);
  const totalStockValue = items.reduce(
    (acc, curr) => acc + curr.current_stock * curr.unit_price,
    0
  );

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName || !newItemCode) return;

    const newItem: InventoryItem = {
      id: `inv-${Date.now()}`,
      code: newItemCode,
      name: newItemName,
      category: newItemCategory,
      current_stock: Number(newItemStock),
      min_stock: Number(newItemMinStock),
      unit: newItemUnit,
      unit_price: Number(newItemPrice),
      location: newItemLocation,
      last_updated: new Date().toISOString(),
    };

    setItems([newItem, ...items]);
    setShowAddModal(false);
    // Reset form
    setNewItemName('');
    setNewItemCode('');
  };

  const handleSaveStockLog = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItemForLog || logQuantity <= 0) return;

    const qty = Number(logQuantity);
    const updatedStock =
      logType === 'in'
        ? selectedItemForLog.current_stock + qty
        : Math.max(0, selectedItemForLog.current_stock - qty);

    setItems((prev) =>
      prev.map((it) =>
        it.id === selectedItemForLog.id
          ? { ...it, current_stock: updatedStock, last_updated: new Date().toISOString() }
          : it
      )
    );

    const newLog: InventoryLog = {
      id: `log-${Date.now()}`,
      item_id: selectedItemForLog.id,
      item_name: selectedItemForLog.name,
      type: logType,
      quantity: qty,
      requester_name: logRequester || 'เจ้าหน้าที่นิติบุคคล',
      department: logDepartment,
      purpose: logPurpose || (logType === 'in' ? 'สั่งซื้อเติมสต็อก' : 'เบิกใช้งานตามปกติ'),
      created_at: new Date().toISOString(),
    };

    setLogs([newLog, ...logs]);
    setShowLogModal(false);
    setSelectedItemForLog(null);
    setLogQuantity(1);
    setLogPurpose('');
    setLogRequester('');
  };

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-cyan-50 text-cyan-600 border border-cyan-200">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">
                3. ระบบจัดการงานสต็อกและวัสดุสิ้นเปลือง (Inventory & Supplies Stock)
              </h2>
              <p className="text-xs text-slate-500">
                ควบคุมคลังพัสดุ อุปกรณ์ช่าง น้ำยาสระว่ายน้ำ หลอดไฟ และบันทึกประวัติการเบิก-จ่าย
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-blue-600/20 cursor-pointer"
          >
            <Plus className="w-4 h-4" /> เพิ่มพัสดุเข้าคลัง
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>รายการพัสดุทั้งหมด</span>
            <Package className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-slate-900 font-mono">{items.length}</p>
          <p className="text-[11px] text-slate-400">ครอบคลุม 6 หมวดหมู่งาน</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>มูลค่าสินค้าคงคลังรวม</span>
            <Sparkles className="w-4 h-4 text-emerald-600" />
          </div>
          <p className="text-2xl font-bold text-emerald-700 font-mono">
            ฿{totalStockValue.toLocaleString()}
          </p>
          <p className="text-[11px] text-emerald-600 font-medium">คำนวณตามราคาต้นทุนเฉลี่ย</p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>สต็อกใกล้หมด (Low Stock)</span>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-amber-600 font-mono">{lowStockItems.length}</p>
          <p className="text-[11px] text-amber-600 font-medium">
            {lowStockItems.length > 0 ? 'ต่ำกว่าเกณฑ์ขั้นต่ำ ต้องสั่งซื้อ' : 'สต็อกพร้อมใช้งาน'}
          </p>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>ประวัติการเบิก-จ่ายสะสม</span>
            <Clock className="w-4 h-4 text-indigo-600" />
          </div>
          <p className="text-2xl font-bold text-indigo-700 font-mono">{logs.length}</p>
          <p className="text-[11px] text-indigo-600">บันทึกอัตโนมัติพร้อมชื่อผู้เบิก</p>
        </div>
      </div>

      {/* Main Stock Table & Search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3 bg-slate-50/50">
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อพัสดุ, รหัสสินค้า, ตำแหน่งจัดเก็บ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
            <Filter className="w-4 h-4 text-slate-400 shrink-0" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="text-xs p-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium"
            >
              <option value="all">ทุกหมวดหมู่ ({items.length})</option>
              <option value="electrical">อุปกรณ์ไฟฟ้า & หลอดไฟ</option>
              <option value="pool_chem">เคมีสระว่ายน้ำ</option>
              <option value="plumbing">ระบบประปา & วาล์ว</option>
              <option value="cleaning">วัสดุทำความสะอาด</option>
              <option value="office">สำนักงาน & เอกสาร</option>
              <option value="security_tools">อุปกรณ์ รปภ. & ป้อมยาม</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
              <tr>
                <th className="p-3.5">รหัสพัสดุ</th>
                <th className="p-3.5">ชื่อรายการ</th>
                <th className="p-3.5">หมวดหมู่</th>
                <th className="p-3.5">ตำแหน่งจัดเก็บ</th>
                <th className="p-3.5 text-center">คงเหลือ / ขั้นต่ำ</th>
                <th className="p-3.5 text-right">ราคาต่อหน่วย</th>
                <th className="p-3.5 text-center">สถานะ</th>
                <th className="p-3.5 text-right">การจัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredItems.map((item) => {
                const isLow = item.current_stock <= item.min_stock;
                return (
                  <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                    <td className="p-3.5 font-mono font-bold text-slate-800">{item.code}</td>
                    <td className="p-3.5">
                      <div className="font-semibold text-slate-900">{item.name}</div>
                      <div className="text-[11px] text-slate-400">อัปเดต: {new Date(item.last_updated).toLocaleDateString('th-TH')}</div>
                    </td>
                    <td className="p-3.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                        {item.category === 'electrical' && 'ไฟฟ้า & หลอดไฟ'}
                        {item.category === 'pool_chem' && 'เคมีสระว่ายน้ำ'}
                        {item.category === 'plumbing' && 'ประปา & วาล์ว'}
                        {item.category === 'cleaning' && 'ทำความสะอาด'}
                        {item.category === 'office' && 'งานสำนักงาน'}
                        {item.category === 'security_tools' && 'อุปกรณ์ รปภ.'}
                      </span>
                    </td>
                    <td className="p-3.5 text-slate-600 font-mono">{item.location}</td>
                    <td className="p-3.5 text-center">
                      <span className="font-bold text-slate-900 font-mono text-sm">
                        {item.current_stock}
                      </span>
                      <span className="text-slate-400 text-xs"> / {item.min_stock} {item.unit}</span>
                    </td>
                    <td className="p-3.5 text-right font-mono text-slate-700">
                      ฿{item.unit_price.toLocaleString()}
                    </td>
                    <td className="p-3.5 text-center">
                      {isLow ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> ใกล้หมด
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> ปกติ
                        </span>
                      )}
                    </td>
                    <td className="p-3.5 text-right space-x-1.5 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setSelectedItemForLog(item);
                          setLogType('out');
                          setShowLogModal(true);
                        }}
                        className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-xs font-semibold border border-amber-200 transition-colors inline-flex items-center gap-1 cursor-pointer"
                      >
                        <ArrowDownLeft className="w-3 h-3 text-amber-700" />
                        <span>เบิกออก</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedItemForLog(item);
                          setLogType('in');
                          setShowLogModal(true);
                        }}
                        className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-semibold border border-emerald-200 transition-colors inline-flex items-center gap-1 cursor-pointer"
                      >
                        <ArrowUpRight className="w-3 h-3 text-emerald-700" />
                        <span>รับเข้า</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock Movement Activity Logs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            ประวัติการเบิก-จ่ายพัสดุและวัสดุสิ้นเปลืองล่าสุด
          </h3>
          <span className="text-xs text-slate-400">บันทึกทั้งหมด {logs.length} รายการ</span>
        </div>

        <div className="divide-y divide-slate-100">
          {logs.slice(0, 6).map((log) => (
            <div key={log.id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      log.type === 'in'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {log.type === 'in' ? '+ รับเข้าคลัง' : '- เบิกใช้งาน'} {log.quantity} หน่วย
                  </span>
                  <span className="font-bold text-slate-800">{log.item_name}</span>
                </div>
                <p className="text-slate-500 text-[11px]">
                  วัตถุประสงค์: {log.purpose} • ผู้เบิก/รับ: <span className="font-medium text-slate-700">{log.requester_name}</span> ({log.department})
                </p>
              </div>

              <span className="text-slate-400 text-[11px] shrink-0 font-mono">
                {new Date(log.created_at).toLocaleString('th-TH')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL: ADD NEW ITEM */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Boxes className="w-5 h-5 text-cyan-400" /> เพิ่มรายการพัสดุเข้าคลังสต็อก
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleAddItem} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">รหัสพัสดุ *</label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น EL-LED-18W"
                    value={newItemCode}
                    onChange={(e) => setNewItemCode(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หมวดหมู่ *</label>
                  <select
                    value={newItemCategory}
                    onChange={(e) => setNewItemCategory(e.target.value as any)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="electrical">อุปกรณ์ไฟฟ้า & หลอดไฟ</option>
                    <option value="pool_chem">เคมีสระว่ายน้ำ</option>
                    <option value="plumbing">ระบบประปา & วาล์ว</option>
                    <option value="cleaning">วัสดุทำความสะอาด</option>
                    <option value="office">งานสำนักงาน</option>
                    <option value="security_tools">อุปกรณ์ รปภ.</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">ชื่อรายการพัสดุ *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น หลอดไฟ LED ขั้ว E27 แสงวอร์มไวท์"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">จำนวนเริ่มต้น</label>
                  <input
                    type="number"
                    min="1"
                    value={newItemStock}
                    onChange={(e) => setNewItemStock(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">จุดสั่งซื้อขั้นต่ำ</label>
                  <input
                    type="number"
                    min="1"
                    value={newItemMinStock}
                    onChange={(e) => setNewItemMinStock(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">หน่วยนับ</label>
                  <input
                    type="text"
                    value={newItemUnit}
                    onChange={(e) => setNewItemUnit(e.target.value)}
                    placeholder="เช่น หลอด, ถัง, แพ็ค"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ราคาต่อหน่วย (บาท)</label>
                  <input
                    type="number"
                    value={newItemPrice}
                    onChange={(e) => setNewItemPrice(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ตำแหน่งจัดเก็บ</label>
                  <input
                    type="text"
                    value={newItemLocation}
                    onChange={(e) => setNewItemLocation(e.target.value)}
                    placeholder="เช่น ตู้ช่าง A1-02"
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold"
                >
                  บันทึกรายการ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: STOCK IN / OUT LOG */}
      {showLogModal && selectedItemForLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">
                  {logType === 'in' ? 'รับพัสดุเข้าสต็อก (+)' : 'บันทึกเบิกพัสดุใช้งาน (-)'}
                </h3>
                <p className="text-xs text-slate-400">{selectedItemForLog.name}</p>
              </div>
              <button onClick={() => setShowLogModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <form onSubmit={handleSaveStockLog} className="p-6 space-y-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex justify-between">
                <span className="text-slate-500">คงเหลือปัจจุบัน:</span>
                <span className="font-bold text-slate-900 font-mono">
                  {selectedItemForLog.current_stock} {selectedItemForLog.unit}
                </span>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">
                  จำนวนที่ต้องการ {logType === 'in' ? 'รับเข้า' : 'เบิก'} ({selectedItemForLog.unit}) *
                </label>
                <input
                  type="number"
                  min="1"
                  max={logType === 'out' ? selectedItemForLog.current_stock : 1000}
                  required
                  value={logQuantity}
                  onChange={(e) => setLogQuantity(Number(e.target.value))}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl font-mono text-base font-bold text-blue-700"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">ชื่อผู้เบิก / ผู้รับ *</label>
                  <input
                    type="text"
                    required
                    placeholder="เช่น ช่างธีระเดช"
                    value={logRequester}
                    onChange={(e) => setLogRequester(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-700 block mb-1">แผนก / ฝ่าย</label>
                  <select
                    value={logDepartment}
                    onChange={(e) => setLogDepartment(e.target.value)}
                    className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                  >
                    <option value="ฝ่ายช่างซ่อมบำรุง">ฝ่ายช่างซ่อมบำรุง</option>
                    <option value="ฝ่ายรักษาความสะอาด">ฝ่ายรักษาความสะอาด</option>
                    <option value="ฝ่ายสวนและภูมิทัศน์">ฝ่ายสวนและภูมิทัศน์</option>
                    <option value="ฝ่ายรักษาความปลอดภัย">ฝ่ายรักษาความปลอดภัย</option>
                    <option value="สำนักงานนิติบุคคล">สำนักงานนิติบุคคล</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-slate-700 block mb-1">วัตถุประสงค์ / สถานที่นำไปใช้ *</label>
                <input
                  type="text"
                  required
                  placeholder="เช่น ซ่อมไฟทางเดินซอย 3 หน้าแปลง 88/24"
                  value={logPurpose}
                  onChange={(e) => setLogPurpose(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className={`px-5 py-2 rounded-xl text-white font-bold shadow-md ${
                    logType === 'in'
                      ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20'
                      : 'bg-amber-600 hover:bg-amber-700 shadow-amber-600/20'
                  }`}
                >
                  {logType === 'in' ? 'ยืนยันรับเข้าสต็อก' : 'ยืนยันการเบิกพัสดุ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
